import api from "./api";
import { v4 as uuidv4 } from "uuid";
import { produce } from "immer";
import deepIs from "deep-is";
import { ExtendedRBush } from "./helpers/ExtendedRBush";
import { arrayToObjectRange } from "./RangeStyle";
import { helper, hfInstance } from "./Cells";
import {
  isRangeWithinRange,
  doRangesOverlap,
  getRowRange,
  getColumnRange,
  applyNumberFilter,
  applyTextFilter,
  applyDateFilter,
  applyTimeFilter,
  stylePatterns,
  unifyRanges,
  isCellWithinRange,
  getKeyByValue,
  tableHeaderValidation,
  updateRange,
  tableTitleValidation,
  hasDuplicates,
  BiMap,
  generateDefaultHeaders,
  ensureMinimumRows,
  rangeDifference,
  isRangeBelowAnotherRange,
  isRangeToLeftOfAnotherRange,
  flattenNamedExpressions,
  removePartUntilSymbol,
  convertToAbsolute,
  isRange,
  isCell,
  sortMatrix,
  mergedNamedExpressions,
  bothRangeInSameColumns,
  getMatrixOfValue,
} from "../utils/util";
import { NumberFormats, getEditModeValue } from "../utils/DataTypeUtils";
import { rangeIndexToLabel, namedRangeLabelToIndex } from "../components/process-design/cell/Utils";
import { rangeLabelToIndex, getCellId } from "../components/process-design/cell/Utils";
import { TableHelper } from "./helpers/TableHelper";

const initialState = {
  tables: [],
  sequenceValue: 0,
  showAddTableDialog: false,
  addNamedRangeVisibility: false,
  nameManagerVisibility: false,
  addNameSource: null, // Track the source of opening Add Named Range modal
  editingNameRange: null,
  filterDialogVisible: false,
  headers: {},
  lastCellIds: {},
  activeTable: null,
  selectedCellId: "",
  hiddenRowIds: {},
  groupedNamedExpressions: {},
  groupedTableNamedExpressions: {},
  namedExpressions: [],
  biMapNameRanges: new BiMap(),
  filterPopupX: 100,
  filterPopupY: 100,
  nameRangeVisibility: false,
  tableTree: new ExtendedRBush(["table"]),
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  actions: {
    setSelectedCellId({ state, getters }, id) {
      state.selectedCellId = id;
    },
    async loadTables({ commit, state, rootState, getters, rootGetters, dispatch }, loadNamedRanges = false) {
      try {
        state.tables = [];
        state.headers = {};
        state.lastCellIds = {};
        state.groupedNamedExpressions = {};
        if (loadNamedRanges) state.groupedTableNamedExpressions = {};
        state.namedExpressions = [];
        const response = await api.get("tables", {
          params: { designId: rootState.cells.current.id },
        });
        state.tables = response.data.tables;
        if (loadNamedRanges) {
          state.tables.forEach((table) => {
            dispatch("createTableNamedRanges", table);
          });
        }
        state.groupedNamedExpressions = response.data.named_expressions;
        setTimeout(() => {
          (async () => {
            try {
              await dispatch("corners/loadTables", null, { root: true });
              await dispatch("corners/loadCorners", null, { root: true });
              await dispatch("satelliteCorner/load", null, { root: true });
              await dispatch("styleCorner/startStyles", null, { root: true });
            } catch (error) {
              console.error("Error during async dispatches:", error);
            }
          })();
        }, 0);
        state.namedExpressions = flattenNamedExpressions(response.data.named_expressions);
        for (let element of state.namedExpressions) {
          if (element.type == "namedRange") {
            commit("SET_NAME_RANGE", { localNameRange: element.localName, defaultNameRange: element.name });
          }
        }

        state.namedExpressions.push(...flattenNamedExpressions(state.groupedTableNamedExpressions));
        dispatch("loadHeaders");
        dispatch("loadLastCellIds");
        dispatch("loadRTree");
        state.sequenceValue = response.data.sequence_value ?? 0;
      } catch (error) {
        console.log(error);
      }
    },
    loadRTree({ state, rootState }) {
      try {
        const currentSheet = rootState.sheets.currentSheet;
        state.tableTree.clear();
        state.tables.forEach((table) => {
          if (currentSheet.id === table.sheetId) {
            const range = arrayToObjectRange(rangeLabelToIndex(table.tableRange));
            state.tableTree.insert({ ...range, data: { table: table } });
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    async loadSheetRelated({ commit, state, rootState, getters, rootGetters, dispatch }) {
      dispatch("loadHeaders");
      dispatch("loadLastCellIds");
    },

    // Updated a range of data used for sorting
    async updateSheetData({ commit, state, dispatch, rootState, getters, rootGetters }, payload) {
      try {
        const { matrix, table, sortedMap } = payload;
        let range = getters.dataRange(table);
        const currentSheet = rootState.sheets.currentSheet;
        // Helper function to get the range for a given row ID
        const getRowRange = (rowId, table) => {
          const { rowMap, columnMap } = rootState.cells.current.design;

          const rowIndex = rowMap[rowId].index;
          if (rowIndex === -1) {
            throw new Error(`Row with ID ${rowId} not found`);
          }
          const startCol = range[0]; // Assuming the range starts from the first column
          const endCol = range[2];
          return [startCol, rowIndex, endCol, rowIndex];
        };

        // Create a new matrix to store the sorted data
        let newMatrix = [];

        // Iterate through the sortedMap
        for (const { sourceRowId, targetRowId } of sortedMap) {
          const sourceRange = getRowRange(targetRowId, table);
          const targetRange = getRowRange(sourceRowId, table);

          // Get the fill range data from HyperFormula
          const fillData = hfInstance.getFillRangeData(
            {
              start: { sheet: currentSheet.sheetId, col: sourceRange[0], row: sourceRange[1] },
              end: { sheet: currentSheet.sheetId, col: sourceRange[2], row: sourceRange[3] },
            },
            {
              start: { sheet: currentSheet.sheetId, col: targetRange[0], row: targetRange[1] },
              end: { sheet: currentSheet.sheetId, col: targetRange[2], row: targetRange[3] },
            }
          );
          // Add the filled data to the new matrix
          newMatrix.push(fillData[0]); // Assuming fillData is a 2D array with one row
        }

        hfInstance.setCellContents({ sheet: currentSheet.sheetId, row: range[1], col: range[0] }, newMatrix);

        const cellIds = rootGetters["cells/rangeMatrix"](range).flat();
        dispatch("cells/toggleRedrawCanvas", "", { root: true });

        dispatch("views/updateViewData", cellIds, { root: true });
        dispatch("cells/persistSelectedCells", { range, value: newMatrix }, { root: true });
      } catch (error) {
        console.error("Detailed error:", error);
      }
    },

    loadLastCellIds({ state, getters, rootGetters }) {
      state.lastCellIds = produce({}, (lastCellIds) => {
        getters.currentSheetTables().forEach((table) => {
          const tableRange = getters.tableIndexRange(table);
          const cellId = rootGetters["cells/getLastCellIdInRange"](tableRange);
          lastCellIds[cellId] = {
            tableName: table.tableName,
          };
        });
      });
    },
    loadHeaders({ state, dispatch, getters, rootGetters }) {
      state.headers = produce({}, (headers) => {
        getters.currentSheetTables().forEach((table) => {
          if (table.headerVisible) {
            const tableHeader = getters.headerRange(table);
            const rangeIds = rootGetters["cells/rangeIds"](tableHeader);
            rangeIds.forEach((cellId) => {
              const filter = table.view.filterApplied ? table.view.filterApplied[cellId] : undefined;
              headers[cellId] = {
                visible: table.filterButtonVisible ?? true,
                tableName: table.tableName,
                filters: filter ? filter : {},
                sort: table.view.sortApplied.cellId === cellId ? table.view.sortApplied.order : "",
              };

              if (filter) {
                dispatch("updateHeaderFilter", { table, cellId, filter });
              }
            });
          }
        });
      });
    },

    async appendRowOrColumnToTable({ commit, state, dispatch, getters, rootState, rootGetters }, cellId) {
      let foundTable = null;
      let foundNextRange = null;
      const cellRange = rootGetters["cells/getRangeByCellId"](cellId);
      for (let table of getters.currentSheetTables()) {
        const nextRanges = getters.nextToTableRange(table);

        if (isCellWithinRange(cellRange, nextRanges.nextRow)) {
          foundTable = table;
          foundNextRange = nextRanges.nextRow;
          break;
        }

        if (isCellWithinRange(cellRange, nextRanges.nextColumn)) {
          foundTable = table;
          foundNextRange = nextRanges.nextColumn;
          break;
        }
      }

      if (!foundTable || !foundTable.allowDynamicExpansion) {
        return;
      }

      //if cells in that range is empty
      const values = rootGetters["cells/getRangeData"]({ range: foundNextRange });
      // Check if any cells in the foundNextRange are not null or empty
      const anyCellsNotEmpty = values.some((row) => row.some((cell) => cell !== null && cell !== ""));
      if (anyCellsNotEmpty) {
        return;
      }
      const tableRange = unifyRanges(rangeLabelToIndex(foundTable.tableRange), foundNextRange);
      dispatch("updateTableDetails", {
        updatedTable: { ...foundTable, tableRange: rangeIndexToLabel(tableRange) },
        oldTable: foundTable,
      });
    },

    async updateTableRangeOnDrag({ commit, state, dispatch, getters, rootState, rootGetters }, table) {
      const oldTableRange = rangeLabelToIndex(table.tableRange);
      const nextRanges = getters.nextToTableRange(table);
      const tableRange = unifyRanges(oldTableRange, nextRanges.nextRow);
      dispatch("updateTableDetails", { updatedTable: { ...table, tableRange }, oldTable: table });
    },
    async updateTableRange({ commit, state, dispatch, getters, rootState, rootGetters }, payload) {
      let { table, range } = payload;

      // check for minimum 3 rows
      range = ensureMinimumRows(range);

      // check if the range overlaps with any table
      const isRangeConflect = getters.tableRangeConflict(payload);
      if (isRangeConflect) {
        return false;
      }

      const rangeIds = rootGetters["cells/rangeIds"](range);
      const keysSet = new Set(Object.keys(rootState.cells.mergedCellIds));

      // Check if any merged cells is there
      const isMergedExists = rangeIds.some((element) => keysSet.has(element));
      if (isMergedExists) {
        return false;
      }

      const newRange = rangeIndexToLabel(range);

      //update last cell IDs
      const cellId = getKeyByValue(state.lastCellIds, { tableName: table.tableName });
      if (cellId) {
        delete state.lastCellIds[cellId];
      }
      if (range) {
        const newCellId = rootGetters["cells/getLastCellIdInRange"](range);
        state.lastCellIds[newCellId] = {
          tableName: table.tableName,
        };
      }
      return newRange;
    },
    async createTable({ commit, state, dispatch, getters, rootState, rootGetters }, tableData) {
      let { tableName, headerVisible, styleId, sequenceValue, totalRowEnabled, ...tableDetails } = tableData;

      if (!tableTitleValidation(tableName)) {
        return;
      }

      let oldExpreession = state.groupedTableNamedExpressions[tableName.toLowerCase()];
      while (oldExpreession) {
        tableName = `Table_${++sequenceValue}`;
        oldExpreession = state.groupedTableNamedExpressions[tableName.toLowerCase()];
      }

      let table = {
        tableName,
        headerVisible,
        defaultTableName: tableName,
        totalRowEnabled,
        styleId,
        sequenceValue,
        ...tableDetails,
      };
      let tableRange = rangeLabelToIndex(table.tableRange);
      tableRange = ensureMinimumRows(tableRange);
      // TODO: alert for range conflict
      const conflicts = getters.tableRangeConflict({ table: null, range: tableRange });
      if (conflicts) return;
      table = { ...table, tableRange: rangeIndexToLabel(tableRange) };
      const headerRange = getters.headerRange(table);
      let allHeadersValidated = true;
      let headerList = [];
      if (headerVisible) {
        const val = rootGetters["cells/getRangeData"]({ range: headerRange });
        val[0].forEach((element) => {
          headerList.push(element);
        });
      }

      const rangeIds = rootGetters["cells/rangeIds"](headerRange);
      const nameCount = {}; // Track occurrences of each name

      for (let i = 1; i <= rangeIds.length; i++) {
        if (!headerVisible || headerList[i - 1] == null || !tableHeaderValidation(headerList[i - 1])) {
          headerList[i - 1] = "Column" + i;
        } else {
          const baseName = headerList[i - 1];
          nameCount[baseName] = (nameCount[baseName] || 0) + 1;
          if (nameCount[baseName] > 1) {
            headerList[i - 1] = baseName + `${nameCount[baseName]}`;
          }
        }
      }

      headerList.forEach((element) => {
        if (!tableHeaderValidation(element)) {
          allHeadersValidated = false;
          return;
        }
      });

      if (!allHeadersValidated || hasDuplicates(headerList)) {
        // show error message
        return;
      }
      table = { ...table, headerList, defaultHeaderList: [...headerList] };

      // TODO: if data has header or not need to shift range
      // TODO: if data has header if not update range + 1
      // TODO: if data has total row if not update range + 1

      dispatch("cells/unmergeCells", "", { root: true });

      const uniqueId = uuidv4();

      const selectedStyle = stylePatterns[styleId];
      dispatch(
        "rangeStyle/addTableStyle",
        {
          data: {
            ...selectedStyle,
            id: uniqueId,
          },
          customRange: tableRange,
        },
        { root: true }
      );

      state.sequenceValue = sequenceValue;

      state.tables.push(table);

      rangeIds.forEach((cellId) => {
        state.headers[cellId] = {
          tableName: table.tableName,
          visible: table.filterButtonVisible ?? true,
          filters: getters.getFilterByCellId(cellId, table),
          sort: table.view.sortApplied.order,
        };
      });
      const cellId = rootGetters["cells/getLastCellIdInRange"](tableRange);
      state.lastCellIds[cellId] = {
        tableName: table.tableName,
      };

      const currentSheet = rootState.sheets.currentSheet;
      hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: headerRange[0], row: headerRange[1] }, [
        headerList,
      ]);

      await dispatch("createTableNamedRanges", {
        id: uniqueId,
        ...table,
        tableName,
        defaultTableName: tableName,
        sequenceValue,
      });

      const response = await api.post("tables", {
        id: uniqueId,
        ...table,
        tableName,
        defaultTableName: tableName,
        sequenceValue,
      });
      setTimeout(() => {
        dispatch("corners/loadCorners", null, { root: true });
      }, 0);
      dispatch("loadRTree");
      dispatch("cells/toggleRedrawCanvas", "", { root: true });
      dispatch("cells/persistSelectedCells", { range: headerRange, value: [headerList] }, { root: true });
    },
    updateTableHeader({ state, getters, rootState, dispatch, rootGetters }, payload) {
      let { table, headersList, defaultHeaderList, oldTable } = payload;
      if (!headersList) headersList = table.headerList;
      const oldHeaderList = oldTable.headerList;
      const tableRange = rangeLabelToIndex(table.tableRange);

      // Validate and replace null or invalid headers with unique and valid column names
      for (let i = 0; i < headersList.length; i++) {
        if (headersList[i] === null || !tableHeaderValidation(headersList[i])) {
          // Added validation here
          let columnIndex = i + 1;
          let newColumnName = `Column${columnIndex}`;

          // Ensure the new column name is unique and valid
          while (headersList.includes(newColumnName) || !tableHeaderValidation(newColumnName)) {
            // Added validation here
            columnIndex++;
            newColumnName = `Column${columnIndex}`;
          }

          headersList[i] = newColumnName;
        }
      }

      const currentSheet = rootState.sheets.currentSheet;
      const headerRange = getters.headerRange(table);
      const oldHeaderRange = getters.headerRange(oldTable);
      const rangeIds = rootGetters["cells/rangeIds"](headerRange);
      const rangeDiffrence = tableRange[2] - tableRange[0] + 1;
      while (rangeDiffrence >= headersList.length) {
        const rowIndex = headerRange[1];
        // take cell value and check if it valid
        const columnIndex = headerRange[2] - (headerRange[2] - headerRange[0] - headersList.length);
        let nextColumnIndex = headersList.length;
        let newColumnName = hfInstance.getCellValue({
          sheet: currentSheet.sheetId,
          row: rowIndex,
          col: columnIndex,
        });
        // Ensure the new column name is unique
        while (!newColumnName || headersList.includes(newColumnName) || !tableHeaderValidation(newColumnName)) {
          nextColumnIndex++;
          newColumnName = `Column${nextColumnIndex}`;
        }

        headersList.push(newColumnName);
      }
      if (rangeDiffrence <= headersList.length) {
        headersList = headersList.slice(0, rangeDiffrence);
      }
      const rangeDiff = rangeDifference(oldHeaderRange, headerRange);
      const deletedRangeIds = [];
      rangeDiff?.forEach((range) => {
        const ids = rootGetters["cells/rangeIds"](range);
        deletedRangeIds.push(...ids);
      });
      deletedRangeIds?.forEach((cellId) => {
        delete state.headers[cellId];
      });
      hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: headerRange[0], row: headerRange[1] }, [
        headersList,
      ]);
      rangeIds.forEach((cellId) => {
        const filter = table.view.filterApplied ? table.view.filterApplied[cellId] : undefined;
        state.headers[cellId] = {
          visible: table.filterButtonVisible ?? true,
          tableName: table.tableName,
          filters: filter ? filter : {},
          sort: table.view.sortApplied.cellId == cellId ? table.view.sortApplied.order : "",
        };

        if (filter) {
          dispatch("updateHeaderFilter", { table, cellId, filter });
        }
      });
      setTimeout(() => {
        dispatch("corners/loadCorners", null, { root: true });
      }, 0);
      dispatch("cells/persistSelectedCells", { range: headerRange, value: [headersList] }, { root: true });
      defaultHeaderList = generateDefaultHeaders(oldHeaderList, defaultHeaderList, headersList);
      return { headerList: headersList, defaultHeaderList };
    },
    async setAndResetTotalTableRow({ getters, dispatch, rootGetters, rootState }, table) {
      const totalRowEnabled = table.totalRowEnabled;
      let rowRange = getters.totalRowRange(table);
      if (totalRowEnabled) {
        const totalRowData = rootGetters["cells/getRangeData"]({ range: rowRange });
        const dataRange = getters.dataRange(table);
        const labelRange = rangeIndexToLabel(dataRange);

        totalRowData[0][0] = "Total";
        totalRowData[0][totalRowData[0].length - 1] = `=SUM(${labelRange})`;
        hfInstance.setCellContents(
          { sheet: rootState.sheets.currentSheet.sheetId, col: rowRange[0], row: rowRange[1] },
          totalRowData
        );
        dispatch(
          "cells/applyColorPattern",
          {
            range: rowRange, // Specify the range
            ...stylePatterns[parseInt(table.styleId) ?? 0],
          },
          { root: true }
        );
      } else {
        await dispatch("cells/updateSelectedCells", { range: rowRange, value: null }, { root: true });
        await dispatch("cells/clearStyleFormats", rowRange, { root: true });
      }

      dispatch("cells/toggleRedrawCanvas", "", { root: true });
    },
    async updateAllTableBelowAddedRows({ getters, dispatch }, payload) {
      const { count, range } = payload;
      const tablesIntersect = getters.allTablesInRange(range);
      const tablesBelowRange = getters.allTablesBelowRange(range);

      for (const element of tablesBelowRange || []) {
        const updatedRange = rangeLabelToIndex(element.tableRange);
        const tableRange = rangeIndexToLabel([
          updatedRange[0],
          updatedRange[1] + count,
          updatedRange[2],
          updatedRange[3] + count,
        ]);
        await dispatch("updateTableDetails", {
          updatedTable: { ...element, tableRange },
          oldTable: element,
        });
      }

      for (const element of tablesIntersect || []) {
        const updatedRange = rangeLabelToIndex(element.tableRange);
        const tableRange = rangeIndexToLabel([
          updatedRange[0],
          updatedRange[1],
          updatedRange[2],
          updatedRange[3] + count,
        ]);
        await dispatch("updateTableDetails", {
          updatedTable: { ...element, tableRange },
          oldTable: element,
        });
      }
    },
    async updateAllTableToLeftAddedColumns({ getters, dispatch, rootGetters }, payload) {
      const { count, range } = payload;
      const tablesIntersect = getters.allTablesInRange(range); // Tables that intersect with the added columns range
      const tablesToLeftRange = getters.allTablesToLeftRange(range); // Tables to the left of the added columns range

      // Update intersecting tables sequentially

      // Update tables to the left of the added columns sequentially
      for (const element of tablesToLeftRange || []) {
        const updatedRange = rangeLabelToIndex(element.tableRange);
        const tableRange = rangeIndexToLabel([
          updatedRange[0] + count, // Update both start and end columns by adding the count
          updatedRange[1],
          updatedRange[2] + count,
          updatedRange[3],
        ]);
        await dispatch("updateTableDetails", {
          updatedTable: { ...element, tableRange },
          oldTable: element,
        });
      }
      for (const element of tablesIntersect || []) {
        const updatedRange = rangeLabelToIndex(element.tableRange);

        // If there is an intersection, reduce the width of the table by the intersected column count
        const tableRange = [
          updatedRange[0], // start column stays the same
          updatedRange[1], // start row stays the same
          updatedRange[2] + count, // reduce end column by intersected column count
          updatedRange[3], // end row stays the same
        ];

        // Prepare the updated table
        const updatedTable = { ...element, tableRange: rangeIndexToLabel(tableRange) };

        // Get the header range for the updated table
        const headerRange = getters.headerRange(updatedTable);

        // Get the data for the header range
        var contents = rootGetters["cells/getRangeData"]({ range: headerRange });

        let headerList = [];
        // If the table still has columns left after the update, get the header list
        if (tableRange[2] >= tableRange[0]) {
          headerList = contents[0]; // Assuming the header is in the first row of the range
        }

        // Dispatch an action to update the table details
        await dispatch("updateTableDetails", {
          updatedTable: { ...updatedTable, headerList },
          oldTable: element,
        });
      }
    },
    async updateAllNamedRangesBelowAddedRows({ getters, dispatch }, payload) {
      const { count, range } = payload;
      const namedRangesIntersect = getters.allNamedRangesInRange(range);
      const namedRangesBelowRange = getters.allNamedRangesBelowRange(range);
      hfInstance.suspendEvaluation();

      if (namedRangesIntersect) {
        for (const element of namedRangesIntersect) {
          const updatedRange = rangeLabelToIndex(element.expression);
          const newExpression = rangeIndexToLabel([
            updatedRange[0],
            updatedRange[1],
            updatedRange[2],
            updatedRange[3] + count, // Expanding row count
          ]);

          dispatch("changeNamedExpression", { ...element, expression: convertToAbsolute(newExpression) });
        }
      }

      if (namedRangesBelowRange) {
        for (const element of namedRangesBelowRange) {
          const updatedRange = rangeLabelToIndex(element.expression);
          const newExpression = rangeIndexToLabel([
            updatedRange[0],
            updatedRange[1] + count, // Shift the range down
            updatedRange[2],
            updatedRange[3] + count,
          ]);
          dispatch("changeNamedExpression", { ...element, expression: convertToAbsolute(newExpression) });
        }
      }
      setTimeout(() => {
        dispatch("corners/loadCorners", null, { root: true });
      }, 0);
      hfInstance.resumeEvaluation();

      await dispatch("persistNamedExpressions");
    },
    async updateAllNamedRangesToLeftAddedColumns({ getters, dispatch }, payload) {
      const { count, range } = payload;
      const namedRangesIntersect = getters.allNamedRangesInRange(range);
      const namedRangesToLeftRange = getters.allNamedRangesToLeftRange(range);
      hfInstance.suspendEvaluation();

      if (namedRangesIntersect) {
        for (const element of namedRangesIntersect) {
          const updatedRange = rangeLabelToIndex(element.expression);
          const newExpression = rangeIndexToLabel([
            updatedRange[0],
            updatedRange[1],
            updatedRange[2] + count, // Expand column range
            updatedRange[3],
          ]);
          dispatch("changeNamedExpression", { ...element, expression: convertToAbsolute(newExpression) });
        }
      }

      if (namedRangesToLeftRange) {
        for (const element of namedRangesToLeftRange) {
          const updatedRange = rangeLabelToIndex(element.expression);
          const newExpression = rangeIndexToLabel([
            updatedRange[0] + count, // Shift range to the right
            updatedRange[1],
            updatedRange[2] + count,
            updatedRange[3],
          ]);

          dispatch("changeNamedExpression", { ...element, expression: convertToAbsolute(newExpression) });
        }
      }
      setTimeout(() => {
        dispatch("corners/loadCorners", null, { root: true });
      }, 0);
      hfInstance.resumeEvaluation();

      dispatch("persistNamedExpressions");
    },

    async updateTableDetails({ state, getters, dispatch }, payload) {
      await dispatch("cells/status", { updating: "updating" }, { root: true });

      try {
        let { updatedTable, oldTable } = payload;
        if (!updatedTable || !oldTable) return;
        const duplicateName =
          updatedTable.tableName !== oldTable.tableName &&
          state.groupedTableNamedExpressions[updatedTable.tableName.toLowerCase()];
        if (!tableTitleValidation(updatedTable.tableName) || duplicateName) {
          return;
        }
        const newStyle = updatedTable.styleId ?? "1";
        if (updatedTable.headerList.length == 0) {
          dispatch("deleteTableWithData", updatedTable._id);
          return;
        }
        // update range
        let tableRange = null;
        if (updatedTable.tableRange !== oldTable.tableRange) {
          tableRange = await dispatch("updateTableRange", {
            table: updatedTable,
            range: rangeLabelToIndex(updatedTable.tableRange),
          });
          updatedTable = { ...updatedTable, tableRange };
        }

        let headerList = updatedTable.headerList;
        let defaultHeaderList = updatedTable.defaultHeaderList;
        // update header
        if (
          !deepIs(updatedTable.headerList, oldTable.headerList) ||
          !bothRangeInSameColumns(rangeLabelToIndex(updatedTable.tableRange), rangeLabelToIndex(oldTable.tableRange))
        ) {
          ({ headerList, defaultHeaderList } = await dispatch("updateTableHeader", {
            table: updatedTable,
            oldTable: oldTable,
            defaultHeaderList: [...updatedTable.defaultHeaderList],
            headersList: [...updatedTable.headerList],
          }));
          updatedTable = { ...updatedTable, headerList: [...headerList], defaultHeaderList: [...defaultHeaderList] };
        }
        helper.style.main.updateTableRangeStyle(
          rangeLabelToIndex(oldTable.tableRange),
          rangeLabelToIndex(updatedTable.tableRange),
          { ...stylePatterns[parseInt(newStyle) ?? 0], id: updatedTable._id }
        );
        // }
        dispatch("cells/toggleRedrawCanvas", "", { root: true });
        if (
          updatedTable.tableRange !== oldTable.tableRange ||
          !deepIs(updatedTable.headerList, oldTable.headerList) ||
          updatedTable.tableName !== oldTable.tableName
        ) {
          dispatch("updateTableNamedRanges", {
            updatedTable,
            oldTable,
          });
        }
        await dispatch("persistTableDetails", { updatedTable });
      } catch (e) {
        console.error(e);
      } finally {
        await dispatch("cells/status", { updating: "updated" }, { root: true });
      }
    },
    async persistTableDetails({ commit, state, dispatch, getters, rootState }, { updatedTable }) {
      commit("UPDATE_TABLE", updatedTable);
      dispatch("loadRTree");
      dispatch("setActiveTable", this.selectedCellId);
      const response = await api.patch("tables/update", {
        ...updatedTable,
        designId: rootState.cells.current.id,
      });
    },
    async convertToRange({ commit, state, dispatch, rootGetters }, table) {
      // deleting table from tables
      await dispatch("deleteTable", table);
      const tableRangeIndex = rangeLabelToIndex(table.tableRange);
      await dispatch("toggleHeaderVisibility", { table, removeData: false });
      const cellId = rootGetters["cells/getLastCellIdInRange"](tableRangeIndex);
      delete state.lastCellIds[cellId];

      //add it to name range
      await dispatch("updateNamedExpression", {
        defaultName: table.tableName.toLowerCase(),
        localName: table.tableName.toLowerCase(),
        sheetId: table.sheetId,
        expression: convertToAbsolute(table.tableRange),
        type: "namedRange",
      });
      await dispatch("persistNamedExpressions");
    },
    toggleHeaderVisibility({ commit, state, dispatch, getters, rootGetters, rootState }, { table, removeData = true }) {
      const headerRange = getters.headerRange(table);
      const headerIds = rootGetters["cells/rangeIds"](headerRange);

      if (removeData) dispatch("cells/updateSelectedCells", { range: headerRange, value: null }, { root: true });

      state.headers = produce(state.headers, (headers) => {
        headerIds.forEach((id) => {
          delete headers[id]; // Delete header from headers
        });
      });
    },

    async toggleFilterButtonVisibility({ dispatch, state, getters, rootGetters }) {
      const activeTable = state.activeTable;
      if (!activeTable) return;

      const headerRange = getters.headerRange(activeTable);
      const headerIds = rootGetters["cells/rangeIds"](headerRange);

      // Toggle the `visible` property of headers
      state.headers = produce(state.headers, (headers) => {
        headerIds.forEach((id) => {
          if (headers[id]) {
            headers[id].visible = !headers[id].visible;
          }
        });
      });

      // Toggle only activeTable.filterButtonVisible
      const updatedTable = {
        ...activeTable,
        filterButtonVisible: !activeTable.filterButtonVisible,
      };

      // Update state.activeTable only
      state.activeTable = updatedTable;

      // Redraw canvas
      dispatch("cells/toggleRedrawCanvas", "", { root: true });

      // Persist updated active table
      await dispatch("persistTableDetails", { updatedTable });
    },
    deleteTableAndHeaderInRange({ commit, state, dispatch, getters, setters }, range) {
      // delete table if it's in range

      const tableList = getters.currentSheetTables();

      tableList.forEach((table) => {
        const tableRange = rangeLabelToIndex(table.tableRange);
        if (isRangeWithinRange(tableRange, range)) {
          dispatch("deleteTableWithData", table._id);
        }
      });

      //TODO: deleted and update header if it's in range
    },
    async deleteTableWithData({ commit, state, dispatch, getters, rootGetters }, id) {
      // remove all data from table range
      const table = getters.tableById(id);
      const tableRangeIndex = rangeLabelToIndex(table.tableRange);
      await dispatch("cells/updateSelectedCells", { range: tableRangeIndex, value: null }, { root: true });

      // remove all data from table headers
      await dispatch("toggleHeaderVisibility", { table });
      await dispatch("deleteTable", table);
      const cellId = rootGetters["cells/getLastCellIdInRange"](tableRangeIndex);
      delete state.lastCellIds[cellId];
      setTimeout(() => {
        dispatch("corners/loadCorners", null, { root: true });
      }, 0);

      // remove all data from styles
      await dispatch("rangeStyle/removeTableStyle", tableRangeIndex, { root: true });
    },
    async deleteTable({ commit, state, dispatch, rootState }, table) {
      await dispatch("clearFilter", table);
      dispatch("deleteTableNamedRanges", table);
      const response = await api.delete("tables/delete", {
        params: {
          tableId: table._id,
          designId: rootState.cells.current.id,
        },
      });
      state.tables = state.tables.filter((item) => item._id !== table._id);
      if (state.activeTable && state.activeTable._id === table._id) {
        state.activeTable = null;
      }
      dispatch("loadRTree");
    },
    deleteTablesInSheet({ commit, state, dispatch, rootState }, { sheetIds }) {
      const tables = state.tables.filter((item) => sheetIds.map(String).includes(String(item.sheetId)));
      tables.forEach(async (table) => {
        await dispatch("deleteTable", table);
      });
    },
    async deleteNamedRangesInSheet({ commit, state, getters, dispatch }, { sheetIds }) {
      const sheetIdStrings = sheetIds.map(String);
      const namedRanges = state.namedExpressions.filter(
        (item) => sheetIdStrings.includes(String(item.sheetId)) && item.type === "namedRange"
      );
      for (const namedRange of namedRanges) {
        // It is not async but if we don't await it, persist will call before it
        await dispatch("deleteNamedRanges", namedRange);
      }
      await dispatch("persistNamedExpressions");
    },
    async deleteTablesInDesign({ commit, state, dispatch }, { ids }) {
      try {
        const resp = await api.post("tables/deleteCollection", { designIds: ids });
        return resp;
      } catch (error) {
        console.error(error);
      }
    },
    setShowAddTableDialog({ commit, state, dispatch }, data) {
      state.showAddTableDialog = data;
    },
    setAddNamedRangeVisibility({ commit, state, dispatch }, data) {
      state.addNamedRangeVisibility = data;
    },
    setNameManagerVisibility({ commit, state, dispatch }, data) {
      state.nameManagerVisibility = data;
    },
    setAddNameSource({ state }, source) {
      state.addNameSource = source;
    },
    setEditingNameRange({ state }, source) {
      state.editingNameRange = source;
    },
    setFilterDialogVisible({ commit, state, dispatch }, data) {
      state.filterDialogVisible = data;
    },
    setFilterDialogPosition({ commit, state, dispatch }, { x, y }) {
      state.filterPopupX = x;
      state.filterPopupY = y;
    },
    setActiveTable({ commit, state, getters, rootGetters }, id) {
      state.activeTable = getters.activeTable(id);
    },
    setActiveTableByName({ commit, state, getters, rootGetters }, name) {
      state.activeTable = state.tables.find((table) => table.tableName?.toLowerCase() === name?.toLowerCase());
    },
    // filter functions

    async addTableFilter({ commit, state, dispatch }, { table, filter }) {
      if (!table.view.filterApplied) {
        table.view.filterApplied = {};
      }
      table.view.filterApplied[filter.cellId] = filter;
      await dispatch("persistTableDetails", { updatedTable: table });
      dispatch("updateHeaderFilter", { table, cellId: filter.cellId, filter });
    },

    async updateTableFilter({ rootGetters, getters, dispatch }, { table, filter }) {
      let cellId = filter.cellId;
      if (!table.view.filterApplied) {
        table.view.filterApplied = {};
      }

      table.view.filterApplied[cellId] = filter;
      await dispatch("persistTableDetails", { updatedTable: table });
      dispatch("updateHeaderFilter", { table, cellId, filter });
      dispatch("cells/toggleRedrawCanvas", "", { root: true });
    },

    async deleteTableFilter({ commit, state, dispatch, getters, rootGetters }, { table, cellId }) {
      if (!cellId) {
        const range = getters.columnRange(cellId);
        // cellId of header of that column is required
        cellId = rootGetters["cells/rangeIds"](range)[1];
      }
      if (table?.view?.filterApplied && table?.view?.filterApplied[cellId]) {
        delete table.view.filterApplied[cellId];
        await dispatch("persistTableDetails", { updatedTable: table });
        dispatch("updateHeaderFilter", { table, cellId, filter: null });
      }
      dispatch("cells/toggleRedrawCanvas", "", { root: true });
    },

    async clearFilter({ commit, state, dispatch }, table) {
      if (table.view.filterApplied) {
        const cellIds = Object.keys(table.view.filterApplied);
        table.view.filterApplied = {};
        await dispatch("persistTableDetails", { updatedTable: table });
        cellIds.forEach((cellId) => {
          dispatch("updateHeaderFilter", { table, cellId, filter: null });
        });
      }
    },
    async sortTable({ commit, state, dispatch, getters, rootGetters }, direction) {
      let matrix = getters.tableDataValues(state.activeTable);
      const columnValues = getters.columnValues(state.selectedCellId);
      const filteredValues = columnValues.filter((item) => rootGetters["views/rowVisibility"](item.rowId));
      const sortedMap = sortMatrix(filteredValues, columnValues, direction);
      await dispatch("updateSheetData", { matrix, table: state.activeTable, sortedMap });
      await dispatch("updateTableSorting", direction);
    },

    async updateTableSorting({ state, dispatch, getters, rootGetters }, sort) {
      const table = state.activeTable;
      let cellId = state.selectedCellId;
      const range = getters.columnRange(cellId);
      // cellId of header of that column is required
      cellId = rootGetters["cells/rangeIds"](range)[1];

      // Use Immer to create a mutable draft of the table and headers
      table.view.sortApplied = produce(table.view.sortApplied, (sortApplied) => {
        sortApplied.cellId = cellId; // Update the sort applied cell ID
        sortApplied.order = sort; // Update the sort order
      });

      // Reset all other sorts in the same table
      const headerRange = getters.headerRange(table);
      const headerIds = rootGetters["cells/rangeIds"](headerRange);

      state.headers = produce(state.headers, (headers) => {
        headerIds.forEach((id) => {
          if (id !== cellId) {
            delete headers[id].sort; // Remove sort from headers not matching the selected cell ID
          }
        });

        // Update the current header with the new sort order
        headers[cellId] = {
          ...headers[cellId], // Spread the existing properties
          sort: table.view.sortApplied.order, // Update the sort property
        };
      });

      dispatch("cells/toggleRedrawCanvas", "", { root: true });
      await dispatch("persistTableDetails", { updatedTable: table });
    },
    clearFilterEffect({ commit, dispatch, state, getters }, { table, cellId }) {
      // Get all hidden row IDs for this cell
      const hiddenRowIds = state.hiddenRowIds[cellId] || [];
      // Show all hidden rows
      if (hiddenRowIds.length > 0) {
        dispatch("views/changeRowsVisiblity", { ids: hiddenRowIds, contentValue: "1" }, { root: true });
      }

      // Clear the hidden row IDs from state
      commit("SET_HIDDEN_ROWIDS", { cellId, hiddenRowIds: [] });
    },
    async validateAndUpdateHeader({ commit, dispatch, state, getters }, { text, cellId, originalText }) {
      // validate header
      const isValid = tableHeaderValidation(text);
      if (!isValid) {
        // TODO: validate Alert need to show
        console.log("not valid");
        return false;
      }
      const table = getters.activeTable(cellId);
      let headers = [...table.headerList];
      const index = headers.findIndex((header) => header + "" === originalText + "");
      if (index >= 0) {
        headers[index] = text;
      }
      //TODO: need to show duplicate error
      if (hasDuplicates(headers)) {
        console.log("duplicate existing");
        return false;
      }
      await dispatch("updateTableDetails", {
        updatedTable: { ...table, headerList: [...headers] },
        oldTable: { ...table },
      });
      return true;
    },

    updateHeaderFilter({ state, getters, dispatch }, { table, cellId, filter }) {
      // Use Immer to create a mutable headers of the headers
      state.headers = produce(state.headers, (headers) => {
        if (headers[cellId]) {
          headers[cellId] = {
            ...headers[cellId],
            filters: filter || {}, // Update filters
          };
        }
      });

      if (filter) {
        const filterCriteria = {
          cellId: cellId,
          type: filter.type,
          condition1: filter.condition1,
          value1: filter.value1,
          condition2: filter.condition2,
          value2: filter.value2,
          logicalOperator: filter.logicalOperator,
          selectedValues: filter.selectedValues,
        };
        const values = getters.columnValuesExcludingHidden(cellId, table);
        let convertedValues = [];
        values.forEach((element) => {
          if (element) convertedValues.push({ ...element, value: getEditModeValue(filter.type, element.value ?? "") });
        });
        dispatch("filterRowValues", {
          values: convertedValues,
          filter: filterCriteria,
          filterType: filter.type,
        });
      } else {
        // If filter is null, we need to clear the filter effect
        dispatch("clearFilterEffect", { table, cellId });
      }
    },
    async filterRowValues({ commit, state, getters, rootGetters, dispatch }, payload) {
      const { values, filter, filterType } = payload;
      let matchRowIds = [];
      let noMatchRowIds = [];

      switch (filterType) {
        case NumberFormats.Number:
          ({ matchRowIds, noMatchRowIds } = applyNumberFilter(filter, values));
          break;
        case NumberFormats.Text:
          ({ matchRowIds, noMatchRowIds } = applyTextFilter(filter, values));
          break;
        case NumberFormats.Date:
          ({ matchRowIds, noMatchRowIds } = await applyDateFilter(filter, values));
          break;
        case NumberFormats.Time:
          ({ matchRowIds, noMatchRowIds } = applyTimeFilter(filter, values));
          break;
        default:
          ({ matchRowIds, noMatchRowIds } = applyTextFilter(filter, values));
      }
      dispatch("changeRowsVisiblityWithIds", { ids: noMatchRowIds, contentValue: "0" });
      dispatch("changeRowsVisiblityWithIds", { ids: matchRowIds, contentValue: "1" });
      commit("SET_HIDDEN_ROWIDS", { cellId: filter.cellId, hiddenRowIds: noMatchRowIds });
    },
    changeRowsVisiblityWithIds({ state, rootGetters, dispatch, rootState }, payload) {
      const { ids, contentValue } = payload;
      dispatch("views/changeRowsVisiblity", { ids: ids, contentValue: contentValue }, { root: true });
    },

    async columnRangeSelection({ commit, state, getters, dispatch, rootState }, cellId) {
      const columnRange = getters.columnRangeExcludeHeader(cellId);
      const range = rangeIndexToLabel(columnRange);
      if (rootState.cells.editCell.formulaRangeSelectionAllowed) {
        dispatch("cells/addFormulaRange", range, { root: true });
      } else {
        dispatch("cells/setFormulaRange", { title: range, activeCellId: cellId }, { root: true });
      }

      dispatch("cells/toggleRedrawCanvas", "", { root: true });
    },

    //Name range Functions
    addNamedExpression({ commit, state, dispatch, rootState, rootGetters }, payload) {
      const { defaultName, sheetId, localName, expression, scope, type, comment } = payload;
      const sheet = rootState.sheets.allSheets.find((item) => item.id == sheetId);
      try {
        const expressionValue = isRange(expression) || isCell(expression) ? `=${sheet.name}!${expression}` : expression;
        hfInstance.addNamedExpression(defaultName, expressionValue, scope);
        commit("ADD_NAMED_EXPRESSION", { defaultName, expression, localName, sheetId: sheet.id, scope, type, comment });
      } catch (error) {
        console.log(error);
      }
    },
    async updateNamedExpressionInSheet({ commit, state, dispatch, rootState, rootGetters }, sheetId) {
      const namedExpressions = state.namedExpressions.filter((expression) => expression.sheetId === sheetId);
      // suspend the evaluation
      hfInstance.suspendEvaluation();
      for (let i = 0; i < namedExpressions.length; i++) {
        const element = namedExpressions[i];
        dispatch("changeNamedExpression", {
          defaultName: element.defaultName,
          localName: element.localName,
          sheetId: element.sheetId,
          expression: element.expression,
        });
      }
      // resume the evaluation
      hfInstance.resumeEvaluation();
    },
    changeNamedExpression({ commit, rootState }, payload) {
      const { defaultName, sheetId, localName, expression, type, scope, oldScope, comment } = payload;
      const sheet = rootState.sheets.allSheets.find((item) => `${item.id}` === `${sheetId}`);
      try {
        const myFormula = hfInstance.getNamedExpression(defaultName, oldScope);
        if (scope !== myFormula?.scope) {
          hfInstance.removeNamedExpression(defaultName, myFormula?.scope);
          const expressionValue =
            isRange(expression) || isCell(expression) ? `=${sheet.name}!${expression}` : expression;

          hfInstance.addNamedExpression(defaultName, expressionValue, scope);
        } else {
          hfInstance.changeNamedExpression(defaultName, `=${sheet.name}!${expression}`, scope);
        }
        commit("UPDATE_NAMED_EXPRESSION", { defaultName, expression, localName, sheetId, type, scope, comment });
      } catch (error) {
        console.log(error);
      }
    },
    removeNamedExpression({ commit, rootState }, { name, type, scope = undefined }) {
      try {
        hfInstance.removeNamedExpression(name, scope);
        commit("REMOVE_NAMED_EXPRESSION", { name, type });
      } catch (error) {
        console.log(error.message);
      }
    },

    async createTableNamedRanges({ commit, state, dispatch, getters, rootState, rootGetters }, table) {
      try {
        const tableHelper = new TableHelper(hfInstance, rootState, rootGetters);
        // Generate named expressions for the given table
        const namedExpressions = tableHelper.generateNamedExpressionsForTable(table);
        const defaultNamedExpressions = tableHelper.generateNamedExpressionsForTable(
          { ...table, tableName: table.defaultTableName },

          [...table.defaultHeaderList]
        );
        const mergedMap = mergedNamedExpressions(defaultNamedExpressions, namedExpressions, table.sheetId);
        hfInstance.suspendEvaluation();
        // Add named expressions
        mergedMap.map((entry) => {
          dispatch("addNamedExpression", {
            defaultName: entry.defaultName,
            localName: entry.name,
            sheetId: table.sheetId,
            expression: entry.expression,
          });
          commit("SET_NAME_RANGE", { localNameRange: entry.name, defaultNameRange: entry.defaultName });
        });

        hfInstance.resumeEvaluation();

        return mergedMap;
      } catch (error) {
        console.log(error);
      }
    },

    async updateNamedExpression(
      { dispatch, state, getters, commit },
      { localName, defaultName, sheetId, expression, scope = undefined, type = "table", comment = undefined }
    ) {
      try {
        let oldExpression = state.namedExpressions.find((item) => item.name == defaultName);
        const oldScope = oldExpression?.scope;

        const oldLocalNameRange = getters.getLocalNameRangeByDefault(defaultName);
        if (oldExpression) {
          oldExpression = oldExpression.expression;
        }

        if (!oldExpression) {
          // add to namedExpressions and biMap
          dispatch("addNamedExpression", {
            defaultName,
            localName,
            sheetId,
            expression,
            scope,
            type,
            comment,
          });
          commit("SET_NAME_RANGE", { localNameRange: localName, defaultNameRange: defaultName });
        } else if (oldExpression !== expression) {
          // update nameRange
          if (oldLocalNameRange !== localName) {
            // update newLocalNameRange
            commit("UPDATE_BY_DEFAULT_NAME", {
              oldDefaultNameRange: defaultName,
              newLocalNameRange: localName,
              newDefaultNameRange: defaultName,
            });
          }
          dispatch("changeNamedExpression", {
            defaultName,
            localName,
            sheetId,
            expression,
            oldScope,
            scope,
            type,
            comment,
          });
        } else if (oldLocalNameRange !== localName || scope !== oldScope) {
          // update newLocalNameRange
          commit("UPDATE_BY_DEFAULT_NAME", {
            oldDefaultNameRange: defaultName,
            newLocalNameRange: localName,
            newDefaultNameRange: defaultName,
          });
          dispatch("changeNamedExpression", {
            defaultName,
            localName,
            oldScope,
            sheetId,
            expression,
            scope,
            type,
            comment,
          });
        }
        await dispatch("cells/toggleRedrawCanvas", "", { root: true });
      } catch (error) {
        console.log(error);
      }
    },
    async persistNamedExpressions({ commit, state, dispatch, rootState }) {
      const id = rootState.cells.current.id;
      // persist only named ranges not tables
      await api.patch(`tables/update-named-expressions`, {
        namedExpressions: state.groupedNamedExpressions,
        designId: id,
      });
    },
    async updateTableNamedRanges({ commit, state, dispatch, getters, rootState, rootGetters }, payload) {
      const { updatedTable, oldTable } = payload;
      const tableHelper = new TableHelper(hfInstance, rootState, rootGetters);
      const sheetId = updatedTable.sheetId;
      const arrayOfKeys = oldTable.headerList;
      const defaultTableName = getters.getDefaultNameRangeByLocal(oldTable.tableName);

      const newNamedExpressions = tableHelper.generateNamedExpressionsForTable(updatedTable);
      const defaultNamedExpressions = tableHelper.generateNamedExpressionsForTable(
        { ...updatedTable, tableName: updatedTable.defaultTableName },
        updatedTable.defaultHeaderList
      );

      const mergedMap = mergedNamedExpressions(defaultNamedExpressions, newNamedExpressions, sheetId);
      hfInstance.suspendEvaluation();

      // Use Promise.all to wait for all updates to complete
      mergedMap.map((entry) => dispatch("updateNamedExpression", { ...entry, localName: entry.name }));

      const expressionsToCheck = state.groupedTableNamedExpressions[defaultTableName.toLowerCase()] || [];
      for (const element of expressionsToCheck.expressions) {
        const name = defaultNamedExpressions.find((item) => item.name === element.name);
        if (!name) {
          dispatch("removeNamedExpression", { name: element.name });
          commit("DELETE_BY_DEFAULT_NAME", element.name);
        }
      }

      // resume the evaluation
      hfInstance.resumeEvaluation();
    },
    async deleteTableNamedRanges({ commit, state, getters, dispatch, rootState }, table) {
      const tableName = getters.getDefaultNameRangeByLocal(table.tableName);
      // Access the named expressions for the specific table
      const expressionsToCheck = state.groupedTableNamedExpressions[tableName] || [];
      // Iterate over the expressions of that table
      hfInstance.suspendEvaluation();
      for (const element of expressionsToCheck.expressions) {
        // Check if the expression name starts with the desired table name
        if (element.name.startsWith(tableName)) {
          dispatch("removeNamedExpression", { name: element.name });
          commit("DELETE_BY_DEFAULT_NAME", element.name);
        }
      }
      hfInstance.resumeEvaluation();
    },
    async deleteNamedRanges({ commit, state, getters, dispatch, rootState }, expression) {
      // Check if the expression name starts with the desired table name
      dispatch("removeNamedExpression", { name: expression.name, type: expression.type, scope: expression.scope });
      commit("DELETE_BY_DEFAULT_NAME", expression.name);
    },
    async applyFormulaForAColumn({ commit, state, getters, dispatch, rootState, rootGetters }, cellId) {
      const currentSheet = rootState.sheets.currentSheet;
      const activeTable = getters.activeTable(cellId);
      if (!activeTable?.applyFormulaToEntireColumn) return rootGetters["cells/getRangeByCellId"](cellId);
      const initialRange = rootGetters["cells/getRangeByCellId"](cellId);
      const dataRange = getters.dataRange(activeTable);

      if (!isRangeWithinRange(initialRange, dataRange)) {
        return;
      }

      const newRange = getters.columnRangeExcludeHeader(cellId);

      const newMatrix = hfInstance.getFillRangeData(
        {
          start: { sheet: currentSheet.sheetId, col: initialRange[0], row: initialRange[1] },
          end: { sheet: currentSheet.sheetId, col: initialRange[2], row: initialRange[3] },
        },
        {
          start: { sheet: currentSheet.sheetId, col: newRange[0], row: newRange[1] },
          end: { sheet: currentSheet.sheetId, col: newRange[2], row: newRange[3] },
        }
      );
      const emptyMatrix = getMatrixOfValue(newRange, null);
      hfInstance.setCellContents({ sheet: currentSheet.sheetId, col: newRange[0], row: newRange[1] }, emptyMatrix);
      const changes = hfInstance.setCellContents(
        { sheet: currentSheet.sheetId, col: newRange[0], row: newRange[1] },
        newMatrix
      );
      setTimeout(() => {
        dispatch("corners/setChangedCells", changes, { root: true });
      }, 0);
      const formula = rootState.cells.current.design.cellsMetaData[cellId]?.formula;
      const columnRange = getters.columnRangeExcludeHeader(cellId, activeTable);
      if (formula) {
        const rangeIds = rootGetters["cells/rangeIds"](columnRange);
        rangeIds.forEach((id) => {
          // TODO: If normal Range is there should get handle properly
          dispatch("cells/editCellMetaData", { id: id, key: "formula", value: formula }, { root: true });
        });
      }

      const cellIds = rootGetters["cells/rangeMatrix"](newRange).flat();
      await dispatch("views/updateViewData", cellIds, { root: true });
      await dispatch("cells/toggleRedrawCanvas", "", { root: true });

      dispatch("cells/persistSelectedCells", { range: newRange, value: newMatrix }, { root: true });
      return newRange;
    },

    toggleNameRanbeVisibility({ commit, state }, data) {
      state.nameRangeVisibility = data ?? !state.nameRangeVisibility;
    },
    async resetState({ state }) {
      Object.assign(state, { ...initialState });
    },
  },
  mutations: {
    UPDATE_TABLE: (state, updatedTable) => {
      const index = state.tables.findIndex((table) => table._id === updatedTable._id);
      if (index !== -1) {
        state.tables.splice(index, 1, updatedTable);
      }
    },
    SET_HIDDEN_ROWIDS(state, { cellId, hiddenRowIds }) {
      state.hiddenRowIds[cellId] = hiddenRowIds;
    },

    SET_NAME_RANGE(state, { localNameRange, defaultNameRange }) {
      state.biMapNameRanges.set(defaultNameRange, localNameRange);
    },
    DELETE_BY_LOCAL_NAME(state, localNameRange) {
      state.biMapNameRanges.deleteByValue(localNameRange);
    },
    DELETE_BY_DEFAULT_NAME(state, defaultNameRange) {
      state.biMapNameRanges.deleteByKey(defaultNameRange);
    },
    UPDATE_BY_LOCAL_NAME(state, { oldLocalNameRange, newLocalNameRange, newDefaultNameRange }) {
      state.biMapNameRanges.updateByValue(oldLocalNameRange, newDefaultNameRange, newLocalNameRange);
    },
    UPDATE_BY_DEFAULT_NAME(state, { oldDefaultNameRange, newLocalNameRange, newDefaultNameRange }) {
      state.biMapNameRanges.updateByKey(oldDefaultNameRange, newDefaultNameRange, newLocalNameRange);
    },
    async ADD_NAMED_EXPRESSION(
      state,
      { defaultName, expression, localName, sheetId, type = "table", scope = undefined, comment = undefined }
    ) {
      const tableName = defaultName.split(".")[0];
      if (type === "table") {
        state.groupedTableNamedExpressions = produce(state.groupedTableNamedExpressions, (draft) => {
          if (!draft[tableName]) {
            draft[tableName] = {
              sheet_id: sheetId,
              type: "table",
              expressions: [
                {
                  name: defaultName,
                  localName,
                  expression,
                },
              ],
            };
          } else {
            draft[tableName].expressions.push({
              name: defaultName,
              localName,
              expression,
            });
          }
        });
      } else {
        state.groupedNamedExpressions = produce(state.groupedNamedExpressions, (draft) => {
          draft[tableName] = {
            sheet_id: sheetId,
            type: "namedRange",
            scope,
            expressions: [
              {
                defaultName,
                localName,
                expression,
              },
            ],
            comment,
          };
        });
      }

      // Update the flat array
      state.namedExpressions.push({ name: defaultName, localName, expression, sheetId, type, scope, comment });
    },

    UPDATE_NAMED_EXPRESSION(
      state,
      { defaultName, expression, localName, sheetId, type = "table", scope = undefined, comment = undefined }
    ) {
      const tableName = defaultName.split(".")[0];
      if (type === "table") {
        state.groupedTableNamedExpressions = produce(state.groupedTableNamedExpressions, (draft) => {
          if (draft[tableName] && draft[tableName].expressions) {
            const index = draft[tableName].expressions.findIndex((expr) => expr.name === defaultName);
            if (index !== -1) {
              draft[tableName].expressions[index] = {
                name: defaultName,
                localName,
                expression,
              };
            }
          }
        });
      } else {
        state.groupedNamedExpressions = produce(state.groupedNamedExpressions, (draft) => {
          // For namedRange type
          if (draft[tableName]) {
            draft[tableName] = {
              ...draft[tableName],
              sheet_id: sheetId,
              expressions: [
                {
                  defaultName,
                  localName,
                  expression,
                },
              ],
              scope,
              comment,
            };
          }
        });
      }

      // Update the flat array
      const flatIndex = state.namedExpressions.findIndex((expr) => expr.name === defaultName);
      if (flatIndex !== -1) {
        state.namedExpressions[flatIndex] = { name: defaultName, localName, expression, sheetId, type, scope, comment };
      }
    },

    REMOVE_NAMED_EXPRESSION(state, { name, type = "table" }) {
      const tableName = name.split(".")[0];
      if (type === "table") {
        state.groupedTableNamedExpressions = produce(state.groupedTableNamedExpressions, (draft) => {
          if (draft[tableName] && draft[tableName].expressions) {
            draft[tableName].expressions = draft[tableName].expressions.filter((expr) => expr.name !== name);

            // Remove the table if no expressions remain
            if (draft[tableName].expressions.length === 0) {
              delete draft[tableName];
            }
          }
        });
      } else {
        state.groupedNamedExpressions = produce(state.groupedNamedExpressions, (draft) => {
          // For namedRange type, simply delete the entire entry
          if (draft[tableName]) {
            delete draft[tableName];
          }
        });
      }

      // Update the flat array
      state.namedExpressions = state.namedExpressions.filter((expr) => expr.name !== name);
    },
  },

  getters: {
    allTables: (state) => state.tables,
    tableNames: (state) => state.tables.map((table) => table.tableName),
    tableHeaders: (state) => (table) => table.headerList,
    selectedCellId: (state) => () => state.selectedCellId,
    currentSheetTables: (state, getters, rootState, rootGetters) => () => {
      const currentSheet = rootState.sheets.currentSheet;
      return state.tables.filter((table) => table.sheetId == currentSheet.id);
    },
    namedRangesForCurrentSheet: (state, _, rootState) => () => {
      const currentSheet = rootState.sheets.currentSheet;
      return state.namedExpressions.filter((value) => value.type === "namedRange" && value.sheetId === currentSheet.id);
    },

    // Named ranges across all sheets
    allNamedRanges: (state) => () => {
      return state.namedExpressions.filter((value) => value.type === "namedRange");
    },
    activeTable: (state, getters, rootState, rootGetters) => (id) => {
      const table = getters.currentSheetTables().find((table) => {
        const tableRangeIndex = rangeLabelToIndex(table.tableRange);
        return isRangeWithinRange(getters.selectedCellRange(id), tableRangeIndex);
      });
      return table ? table : null;
    },
    tableIndexRange: (state) => (table) => {
      return rangeLabelToIndex(table.tableRange);
    },
    tableInRtree: (state) => (range) => {
      return state.tableTree.search(arrayToObjectRange(range));
    },
    tableByRange: (state) => (cellRange) => {
      const table = state.tables.find((tb) => {
        const tableRangeIndex = rangeLabelToIndex(tb.tableRange);
        return isRangeWithinRange(cellRange, tableRangeIndex);
      });
      return table ? table : null;
    },
    tableByRangeInSheet: (state, getters) => (cellRange) => {
      const table = getters.currentSheetTables().find((table) => {
        const tableRangeIndex = rangeLabelToIndex(table.tableRange);
        return isRangeWithinRange(cellRange, tableRangeIndex);
      });
      return table ? table : null;
    },
    allTablesInRange: (state, getters) => (range) => {
      const tables = getters.currentSheetTables().filter((table) => {
        const tableRangeIndex = rangeLabelToIndex(table.tableRange);
        return doRangesOverlap(range, tableRangeIndex);
      });
      return tables.length > 0 ? tables : null;
    },
    allTablesBelowRange: (state, getters) => (range) => {
      const tables = getters.currentSheetTables().filter((table) => {
        const tableRangeIndex = rangeLabelToIndex(table.tableRange);
        return isRangeBelowAnotherRange(tableRangeIndex, range);
      });
      return tables.length > 0 ? tables : null;
    },
    allTablesToLeftRange: (state, getters) => (range) => {
      const tables = getters.currentSheetTables().filter((table) => {
        const tableRangeIndex = rangeLabelToIndex(table.tableRange);
        return isRangeToLeftOfAnotherRange(range, tableRangeIndex);
      });
      return tables.length > 0 ? tables : null;
    },
    allNamedRangesInRange: (state, getters) => (range) => {
      const namedRanges = getters.namedRangesForCurrentSheet().filter((namedRange) => {
        const namedRangeIndex = rangeLabelToIndex(namedRange.expression);
        return doRangesOverlap(range, namedRangeIndex);
      });
      return namedRanges.length > 0 ? namedRanges : null;
    },

    allNamedRangesBelowRange: (state, getters) => (range) => {
      const namedRanges = getters.namedRangesForCurrentSheet().filter((namedRange) => {
        const namedRangeIndex = rangeLabelToIndex(namedRange.expression);
        return isRangeBelowAnotherRange(namedRangeIndex, range);
      });
      return namedRanges.length > 0 ? namedRanges : null;
    },
    allNamedRangesToLeftRange: (state, getters) => (range) => {
      const namedRanges = getters.namedRangesForCurrentSheet().filter((namedRange) => {
        const namedRangeIndex = rangeLabelToIndex(namedRange.expression);
        return isRangeToLeftOfAnotherRange(range, namedRangeIndex);
      });
      return namedRanges.length > 0 ? namedRanges : null;
    },
    tableById: (state) => (id) => {
      const table = state.tables.find((table) => {
        return table._id === id;
      });
      return table ? table : null;
    },
    tableByName: (state) => (name) => {
      const table = state.tables.find((table) => {
        return table.tableName === name;
      });
      return table ? table : null;
    },
    selectedCellRange: (state, getters, rootState, rootGetters) => (cellId) => {
      return rootGetters["cells/getRangeByCellId"](cellId ?? state.selectedCellId);
    },
    columnIndexInTable: (state, getters, rootState, rootGetters) => () => {
      const table = getters.tableByRangeInSheet(getters.selectedCellRange());
      const range = getters.dataRange(table);
      return getters.selectedCellRange()[0] - range[0];
    },
    rowIndexInTable: (state, getters, rootState, rootGetters) => (cellId) => {
      const table = getters.tableByRangeInSheet(getters.selectedCellRange());
      const range = getters.dataRange(table);
      return getters.selectedCellRange()[1] - range[1];
    },
    headerRange: (state) => (table) => {
      const range = rangeLabelToIndex(table.tableRange);
      return [
        range[0], // start column
        range[1] + 1, // start row (header row)
        range[2], // end column
        range[1] + 1, // end row (header row)
      ];
    },
    tableRangeConflict: (state, getters) => (payload) => {
      const { table, range } = payload;
      const ownTable = table;
      const conflict = getters.currentSheetTables().some((table) => {
        if (ownTable === null || table._id !== ownTable._id) {
          const tableRange = rangeLabelToIndex(table.tableRange);
          return doRangesOverlap(range, tableRange);
        }
        return false;
      });
      return conflict;
    },
    tableHeaderConflict: (state, getters) => (range) => {
      const conflict = getters.currentSheetTables().some((table) => {
        const headerRange = getters.headerRange(table);
        return doRangesOverlap(range, headerRange);
      });
      return conflict;
    },
    tableTotalConflict: (state, getters) => (range) => {
      const conflict = getters.currentSheetTables().some((table) => {
        const headerRange = getters.totalRowRange(table);
        return doRangesOverlap(range, headerRange);
      });
      return conflict;
    },
    totalRowRange: (state) => (table) => {
      const range = rangeLabelToIndex(table.tableRange);
      return [
        range[0], // start column
        range[1], // start row (total row)
        range[2], // end column
        range[1], // end row (total row)
      ];
    },
    totalRowEnabled: (state) => (table) => {
      return table.totalRowEnabled;
    },
    dataRange: (state, getters) => (table) => {
      const range = rangeLabelToIndex(table.tableRange);
      return [
        range[0], // start column
        range[1] + 2, // start row (excluding header row)
        range[2], // end column
        range[3], // end row
      ];
    },
    nextToTableRange: (state) => (table) => {
      const range = rangeLabelToIndex(table.tableRange);
      return {
        nextRow: [range[0], range[3] + 1, range[2], range[3] + 1],
        nextColumn: [range[2] + 1, range[1], range[2] + 1, range[3]],
      };
    },
    tableRowRange: (state, getters) => () => {
      const table = getters.tableByRangeInSheet(getters.selectedCellRange());
      const tableRangeIndex = rangeLabelToIndex(table.tableRange);
      return getRowRange(tableRangeIndex, getters.selectedCellRange());
    },
    columnRange: (state, getters) => (cellId) => {
      const table = getters.tableByRangeInSheet(getters.selectedCellRange(cellId));
      const tableRange = rangeLabelToIndex(table.tableRange);
      let cellRange = getters.selectedCellRange(cellId);
      return getColumnRange(tableRange, cellRange);
    },
    columnRangeExcludeHeader:
      (state, getters) =>
      (cellId, table = null) => {
        if (!table) table = getters.tableByRangeInSheet(getters.selectedCellRange(cellId));
        const dataRange = getters.dataRange(table);
        let cellRange = getters.selectedCellRange(cellId);
        // if user send an id from header or total it will return data column range
        if (cellRange[1] == dataRange[1] - 1 || (table.totalRowEnabled && cellRange[1] == dataRange[1] - 2)) {
          cellRange[1] = dataRange[1];
          cellRange[3] = dataRange[1];
        }
        return getColumnRange(dataRange, cellRange);
      },
    columnValues: (state, getters, rootState, rootGetters) => (cellId) => {
      const range = getters.columnRangeExcludeHeader(cellId);
      const rowIds = rootGetters["cells/rowsInRange"](range);

      const values = rootGetters["cells/getRangeValues"]({ range: range });
      let rowMaps = [];
      rowIds.forEach((rowId, index) => {
        const value = values[index][0];
        rowMaps.push({ rowId, value });
      });
      return rowMaps;
    },

    columnValuesArray: (state, getters, rootState, rootGetters) => (cellId) => {
      const range = getters.columnRangeExcludeHeader(cellId);
      const values = rootGetters["cells/getRangeValues"]({ range: range });
      return values;
    },

    columnValuesExcludingHidden: (state, getters, rootState, rootGetters) => (cellId, table) => {
      const range = getters.columnRangeExcludeHeader(cellId);
      const values = getters.columnValues(cellId);
      const hiddenRows = getters.allHiddenRows(table ?? state.activeTable, cellId) || [];

      let rowMaps = [];
      values.forEach((element) => {
        if (!hiddenRows.includes(element.rowId)) {
          rowMaps.push({ rowId: element.rowId, value: element.value });
        }
      });
      return rowMaps;
    },

    allHiddenRows: (state, getters, rootState, rootGetters) => (table, id) => {
      const range = getters.headerRange(table);
      const rangeIds = rootGetters["cells/rangeIds"](range);
      let rowIds = new Set();
      rangeIds.forEach((cellId) => {
        if (state.hiddenRowIds[cellId]) {
          state.hiddenRowIds[cellId].forEach((rowId) => {
            rowIds.add(rowId);
          });
        }
      });
      if (state.hiddenRowIds[id]) {
        state.hiddenRowIds[id].forEach((rowId) => {
          rowIds.delete(rowId);
        });
      }
      return Array.from(rowIds);
    },
    tableDataValues: (state, getters, rootState, rootGetters) => (table) => {
      const range = getters.dataRange(table);
      return rootGetters["cells/getRangeData"]({ range: range });
    },
    getFilterByCellId: (state, getters, rootState, rootGetters) => (cellId, table) => {
      return table.view.filterApplied ? table.view.filterApplied[cellId] : undefined;
    },
    getAllTableNameRanges: (state, getters, rootState, rootGetters) => () => {
      const currentSheet = rootState.sheets.currentSheet;

      return state.namedExpressions
        .filter((item) => {
          const availableInScope = item.scope === undefined || item.scope === currentSheet.sheetId;
          if (!item.name.includes(".") && availableInScope) {
            return true;
          } else {
            return false;
          }
        })
        .map((item) => item.name);
    },
    getAllTableAndNamedRanges: (state, getters, rootState, rootGetters) => () => {
      return state.namedExpressions.filter((item) => !item.name.includes("."));
    },
    getTableColumnNameRanges: (state, getters, rootState, rootGetters) => (payload) => {
      const { tableName, onlyColumns } = { ...payload };
      return state.namedExpressions
        .filter((item) => {
          const name = item.name;
          if (!name.startsWith(tableName)) {
            return false;
          }
          const nameWithoutTable = name.slice(tableName.length);
          const validFormat1 = /^[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
          const validFormat2 = /^[a-zA-Z0-9_]+\.\.[a-zA-Z0-9_]+$/.test(name);
          const invalidFormat1 = /^[a-zA-Z0-9_]+\.\.[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
          const invalidFormat2 = /^[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
          return onlyColumns
            ? validFormat1 && !validFormat2
            : (validFormat1 || validFormat2) && !invalidFormat1 && !invalidFormat2;
        })
        .map((item) => {
          const name = getters.getLocalNameRangeByDefault(item.name);
          const parts = name.split(".");
          let columnName = parts.slice(1).join(".");
          if (name.includes("..")) {
            columnName = columnName.replace(".", "");
            columnName = `#${columnName}`;
          }
          return columnName;
        });
    },
    getNamedExpressionByRange: (state) => (range) => {
      return state.namedExpressions.find((item) => {
        // Extract range part from the expression (after '=' and removing sheet name)
        const expressionRange = removePartUntilSymbol(item.expression, "!");

        // Normalize both ranges by removing any $ symbols
        const normalizedExpressionRange = expressionRange.replace(/\$/g, "");
        const normalizedRange = range.replace(/\$/g, "");

        // Check if the ranges match
        return normalizedExpressionRange === normalizedRange;
      });
    },
    getNamedExpression: (state, getters, rootState, rootGetters) => (name) => {
      return state.namedExpressions.find((item) => item.name === name);
    },
    namedRangeToIndex: (state, getters, rootState, rootGetters) => (name) => {
      const namedRange = getters.getNamedExpression(name);
      if (namedRange) return namedRangeLabelToIndex(namedRange.expression) ?? "";
      return null;
    },
    getColumnCombinationsNamedExpression: (state, getters, rootState, rootGetters) => (tableName) => {
      return state.namedExpressions.filter((item) => {
        const name = item.name;
        if (!name.startsWith(tableName)) {
          return false;
        }
        const validFormat1 = /^[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
        const invalidFormat2 = /^[a-zA-Z0-9_]+\.\.[a-zA-Z0-9_]+$/.test(name);
        const invalidFormat1 = /^[a-zA-Z0-9_]+\.\.[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
        const validFormat2 = /^[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+$/.test(name);
        return (validFormat1 || validFormat2) && !invalidFormat1 && !invalidFormat2;
      });
    },

    headerRangeIntersectionWithRange: (state, getters, rootState, rootGetters) => (payload) => {
      const { tableName, range } = payload;
      const allNameRanges = getters.getColumnCombinationsNamedExpression(tableName);
      const table = getters.tableByRangeInSheet(range);
      if (!table) return null;
      let namedRange = null;
      allNameRanges.forEach((nameRange) => {
        let indexRange = getters.namedRangeToIndex(nameRange.name);
        if (indexRange[0] == range[0] && indexRange[2] == range[2]) {
          namedRange = nameRange;
        }
      });
      return namedRange;
    },
    namedRangeIntersectionWithRange: (state, getters, rootState, rootGetters) => (payload) => {
      const { tableName, range } = payload;
      const allNameRanges = getters.getColumnCombinationsNamedExpression(tableName);
      const table = getters.tableByRangeInSheet(range);
      if (!table) return null;
      const dataRange = getters.dataRange(table);
      if (!isRangeWithinRange(range, dataRange)) return;
      let namedRange = null;
      allNameRanges.forEach((nameRange) => {
        let indexRange = getters.namedRangeToIndex(nameRange.name);
        if (indexRange[0] == range[0] && indexRange[2] == range[2]) {
          namedRange = nameRange;
        }
      });
      return namedRange;
    },
    getDefaultNameRangeByLocal: (state) => (localNameRange) => {
      return state.biMapNameRanges.getByValue(localNameRange?.toLowerCase()) ?? "";
    },
    getLocalNameRangeByDefault: (state) => (defaultNameRange) => {
      return state.biMapNameRanges.getByKey(defaultNameRange?.toLowerCase()) ?? "";
    },
    getBiMapNameRange: (state) => () => {
      return state.biMapNameRanges.getKeyToValueMap();
    },
    getNameRangeVisibility: (state) => () => {
      return state.nameRangeVisibility;
    },
  },
};
